// FracCalendar.tsx
import React, { useState, useMemo, useRef, useEffect } from 'react';
import Header from './Header.tsx';
import { useTheme } from '../hooks/useTheme.ts';
import styles from './FracCalendar.module.css';
import { FiPlus, FiX, FiChevronUp, FiChevronDown, FiEdit3, FiCalendar, FiDollarSign, FiFileText, FiTruck, FiMapPin, FiUser, FiActivity } from 'react-icons/fi';
import { ToastContainer, toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

// Types
interface FracJob {
  id: string;
  fleet: string;
  district: District;
  operator: string;
  resource: string;
  startDate: Date;
  endDate: Date;
  expectedStagesPerDay: number;
  actualStagesPerDay: number;
  revenuePerStage: number;
  status: 'scheduled' | 'in-progress' | 'completed' | 'cancelled';
  notes?: string;
}

type District = 'Northeast' | 'Vernal' | 'Longview' | 'Pleasanton' | 'Midland';

// District Colors - Using Machina-inspired theme with existing palette
const DISTRICT_COLORS: Record<District, { bg: string; border: string; text: string }> = {
  Northeast: { bg: '#3E76FE', border: '#2A5AE6', text: '#ffffff' },
  Vernal: { bg: '#8CC540', border: '#72A32C', text: '#ffffff' },
  Longview: { bg: '#00BCD4', border: '#0097A7', text: '#ffffff' },
  Pleasanton: { bg: '#F9C844', border: '#E5B73D', text: '#303335' },
  Midland: { bg: '#BD9EFF', border: '#A684E6', text: '#303335' },
};

// Sample data
const generateSampleData = (): FracJob[] => {
  const operators = [
    'ConocoPhillips', 'Devon Energy', 'EOG Resources', 'Magnolia Oil', 
    'SM Energy', 'Exco Resources', 'Apache Corporation', 'Crescent Energy',
    'HighPeak Energy', 'Verdun Oil Company', 'Infinity Natural Resources'
  ];
  
  const fleets = [
    '1000|18', '1000|20', '1000|38', '1000|41', '1000|50', '2100|37', 
    '2100|38', '2100|39.01', '2100|99.01', '4000|09', '4000|12', 
    '4000|17', '5000|04', '5000|08', '5000|09', '5000|23', '5000|25',
    '5000|27', '5000|48', '6100|03', '6100|19', '6100|26'
  ];
  
  const districts: District[] = ['Northeast', 'Vernal', 'Longview', 'Pleasanton', 'Midland'];
  
  const jobs: FracJob[] = [];
  const baseDate = new Date('2026-01-01');
  
  fleets.forEach((fleet, index) => {
    // Generate 1-3 jobs per fleet
    const numJobs = Math.floor(Math.random() * 3) + 1;
    let lastEndDate = new Date(baseDate);
    
    for (let i = 0; i < numJobs; i++) {
      const startDate = new Date(lastEndDate);
      startDate.setDate(startDate.getDate() + Math.floor(Math.random() * 14) + 1);
      
      const duration = Math.floor(Math.random() * 30) + 7;
      const endDate = new Date(startDate);
      endDate.setDate(endDate.getDate() + duration);
      
      const district = districts[index % districts.length];
      
      jobs.push({
        id: `job-${fleet}-${i}`,
        fleet,
        district,
        operator: operators[Math.floor(Math.random() * operators.length)],
        resource: `${operators[Math.floor(Math.random() * operators.length)]} ${['Pad', 'Well', 'Ranch', 'Unit'][Math.floor(Math.random() * 4)]} ${Math.floor(Math.random() * 100)}`,
        startDate,
        endDate,
        expectedStagesPerDay: Math.round((Math.random() * 8 + 2) * 100) / 100,
        actualStagesPerDay: Math.round((Math.random() * 8 + 1) * 100) / 100,
        revenuePerStage: Math.round((Math.random() * 50000 + 30000) * 100) / 100,
        status: ['scheduled', 'in-progress', 'completed'][Math.floor(Math.random() * 3)] as FracJob['status'],
        notes: Math.random() > 0.7 ? 'Priority job' : undefined,
      });
      
      lastEndDate = endDate;
    }
  });
  
  return jobs;
};

// Helper functions
const addDays = (date: Date, days: number): Date => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

const getDaysBetween = (start: Date, end: Date): number => {
  const msPerDay = 24 * 60 * 60 * 1000;
  return Math.round((end.getTime() - start.getTime()) / msPerDay);
};

const formatDate = (date: Date): string => {
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const formatFullDate = (date: Date): string => {
  return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
};

// Operators list for dropdown
const OPERATORS = [
  'ConocoPhillips',
  'Devon Energy',
  'EOG Resources',
  'Magnolia Oil',
  'SM Energy',
  'Exco Resources',
  'Apache Corporation',
  'Crescent Energy',
  'HighPeak Energy',
  'Verdun Oil Company',
  'Infinity Natural Resources',
  'Pioneer Natural Resources',
  'Occidental Petroleum',
  'Diamondback Energy',
  'Marathon Oil',
  'Chesapeake Energy',
  'Continental Resources',
  'Ovintiv',
  'APA Corporation',
  'Coterra Energy'
];

// Modal Component
interface FracJobModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (job: Omit<FracJob, 'id'> | FracJob) => void;
  onDelete?: () => void;
  job?: FracJob;
  fleets: string[];
  mode: 'add' | 'edit';
}

const FracJobModal: React.FC<FracJobModalProps> = ({ isOpen, onClose, onSave, onDelete, job, fleets, mode }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const modalRef = useRef<HTMLDivElement>(null);
  
  const [formData, setFormData] = useState({
    fleet: job?.fleet || fleets[0] || '',
    district: job?.district || 'Northeast' as District,
    operator: job?.operator || '',
    resource: job?.resource || '',
    startDate: job?.startDate || new Date(),
    endDate: job?.endDate || addDays(new Date(), 14),
    expectedStagesPerDay: job?.expectedStagesPerDay || 6,
    actualStagesPerDay: job?.actualStagesPerDay || 0,
    revenuePerStage: job?.revenuePerStage || 40000,
    status: job?.status || 'scheduled' as FracJob['status'],
    notes: job?.notes || '',
  });

  useEffect(() => {
    if (job) {
      setFormData({
        fleet: job.fleet,
        district: job.district,
        operator: job.operator,
        resource: job.resource,
        startDate: job.startDate,
        endDate: job.endDate,
        expectedStagesPerDay: job.expectedStagesPerDay,
        actualStagesPerDay: job.actualStagesPerDay,
        revenuePerStage: job.revenuePerStage,
        status: job.status,
        notes: job.notes || '',
      });
    }
  }, [job]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.fleet || !formData.operator || !formData.resource) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (formData.startDate >= formData.endDate) {
      toast.error('End date must be after start date');
      return;
    }
    
    if (job) {
      onSave({ ...formData, id: job.id });
    } else {
      onSave(formData);
    }
  };

  const handleClickOutside = (e: React.MouseEvent) => {
    if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const districts: District[] = ['Northeast', 'Vernal', 'Longview', 'Pleasanton', 'Midland'];

  return (
    <div className={styles.modalOverlay} onClick={handleClickOutside}>
      <div className={`${styles.modal} ${isDark ? styles.modalDark : ''}`} ref={modalRef}>
        <div className={mode === 'edit' ? styles.modalHeaderGreen : styles.modalHeader}>
          <h2>
            {mode === 'add' ? <><FiPlus size={20} /> Add Frac Job</> : <><FiEdit3 size={20} /> Edit Frac Job</>}
          </h2>
          <button className={styles.modalClose} onClick={onClose}>
            <FiX size={20} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className={styles.modalForm}>
          {/* Job Details Section */}
          <div className={styles.formSection}>
            <div className={styles.formSectionTitle}>
              <FiTruck size={14} /> Job Details
            </div>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>Fleet <span className={styles.required}>*</span></label>
                <select 
                  value={formData.fleet} 
                  onChange={(e) => setFormData({ ...formData, fleet: e.target.value })}
                  required
                >
                  {fleets.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
              </div>
              <div className={styles.formGroup}>
                <label>District <span className={styles.required}>*</span></label>
                <select 
                  value={formData.district} 
                  onChange={(e) => setFormData({ ...formData, district: e.target.value as District })}
                  required
                >
                  {districts.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>Operator <span className={styles.required}>*</span></label>
                <select 
                  value={formData.operator}
                  onChange={(e) => setFormData({ ...formData, operator: e.target.value })}
                  required
                >
                  <option value="">Select Operator...</option>
                  {OPERATORS.sort().map(op => (
                    <option key={op} value={op}>{op}</option>
                  ))}
                </select>
              </div>
              <div className={styles.formGroup}>
                <label><FiActivity size={12} /> Status</label>
                <select 
                  value={formData.status} 
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as FracJob['status'] })}
                >
                  <option value="scheduled">Scheduled</option>
                  <option value="in-progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            </div>

            <div className={styles.formGroup}>
              <label><FiMapPin size={12} /> Resource / Pad Name <span className={styles.required}>*</span></label>
              <input 
                type="text" 
                value={formData.resource}
                onChange={(e) => setFormData({ ...formData, resource: e.target.value })}
                placeholder="e.g., Comstock Campbell 9-16-21HC 1-Alt/2-Alt"
                required
              />
            </div>
          </div>

          {/* Schedule Section */}
          <div className={styles.formSection}>
            <div className={styles.formSectionTitle}>
              <FiCalendar size={14} /> Schedule
            </div>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>Start Date</label>
                <DatePicker
                  selected={formData.startDate}
                  onChange={(date: Date | null) => date && setFormData({ ...formData, startDate: date })}
                  dateFormat="MM/dd/yyyy"
                  className={styles.datePicker}
                />
              </div>
              <div className={styles.formGroup}>
                <label>End Date</label>
                <DatePicker
                  selected={formData.endDate}
                  onChange={(date: Date | null) => date && setFormData({ ...formData, endDate: date })}
                  dateFormat="MM/dd/yyyy"
                  className={styles.datePicker}
                  minDate={formData.startDate}
                />
              </div>
            </div>
          </div>

          {/* Performance & Revenue Section */}
          <div className={styles.formSection}>
            <div className={styles.formSectionTitle}>
              <FiDollarSign size={14} /> Performance & Revenue
            </div>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>Expected Stages/Day</label>
                <input 
                  type="number" 
                  step="0.01"
                  value={formData.expectedStagesPerDay}
                  onChange={(e) => setFormData({ ...formData, expectedStagesPerDay: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div className={styles.formGroup}>
                <label>Actual Stages/Day</label>
                <input 
                  type="number" 
                  step="0.01"
                  value={formData.actualStagesPerDay}
                  onChange={(e) => setFormData({ ...formData, actualStagesPerDay: parseFloat(e.target.value) || 0 })}
                />
              </div>
            </div>
            <div className={styles.formGroup}>
              <label>Revenue/Stage ($)</label>
              <input 
                type="number" 
                step="0.01"
                value={formData.revenuePerStage}
                onChange={(e) => setFormData({ ...formData, revenuePerStage: parseFloat(e.target.value) || 0 })}
              />
            </div>
          </div>

          {/* Notes Section */}
          <div className={styles.formSection}>
            <div className={styles.formSectionTitle}>
              <FiFileText size={14} /> Additional Notes
            </div>
            <div className={styles.formGroup}>
              <textarea 
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Add any additional notes or comments..."
                rows={3}
              />
            </div>
          </div>

          <div className={styles.modalActions}>
            {mode === 'edit' && onDelete && (
              <button type="button" className={styles.deleteButton} onClick={onDelete}>
                Delete
              </button>
            )}
            <button type="button" className={styles.cancelButton} onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className={mode === 'edit' ? styles.saveButtonGreen : styles.saveButton}>
              {mode === 'add' ? 'Add Job' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Confirm Delete Modal
interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  job?: FracJob;
}

const ConfirmDeleteModal: React.FC<ConfirmModalProps> = ({ isOpen, onClose, onConfirm, job }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const modalRef = useRef<HTMLDivElement>(null);

  if (!isOpen || !job) return null;

  return (
    <div className={styles.modalOverlay} onClick={(e) => {
      if (modalRef.current && !modalRef.current.contains(e.target as Node)) onClose();
    }}>
      <div className={`${styles.modal} ${styles.confirmModal} ${isDark ? styles.modalDark : ''}`} ref={modalRef}>
        <div className={styles.modalHeader}>
          <h2>Confirm Deletion</h2>
          <button className={styles.modalClose} onClick={onClose}>
            <FiX size={20} />
          </button>
        </div>
        <div className={styles.confirmContent}>
          <p>Are you sure you want to delete this frac job?</p>
          <div className={styles.confirmDetails}>
            <strong>{job.operator}</strong>
            <span>{job.resource}</span>
            <span>{formatFullDate(job.startDate)} - {formatFullDate(job.endDate)}</span>
          </div>
          <p className={styles.confirmWarning}>This action cannot be undone.</p>
        </div>
        <div className={styles.modalActions}>
          <button type="button" className={styles.cancelButton} onClick={onClose}>
            Cancel
          </button>
          <button type="button" className={styles.deleteButton} onClick={onConfirm}>
            Delete Job
          </button>
        </div>
      </div>
    </div>
  );
};

// Main FracCalendar Component
function FracCalendar() {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  
  // Data state
  const [jobs, setJobs] = useState<FracJob[]>(() => generateSampleData());
  
  // View state - Two date pickers for start and end
  const [viewStartDate, setViewStartDate] = useState<Date>(new Date('2025-12-31'));
  const [viewEndDate, setViewEndDate] = useState<Date>(new Date('2026-03-31'));
  
  // Filter state - initialize with all districts selected
  const districts: District[] = ['Northeast', 'Vernal', 'Longview', 'Pleasanton', 'Midland'];
  const [selectedDistricts, setSelectedDistricts] = useState<District[]>([...districts]);
  const [selectedFleets, setSelectedFleets] = useState<string[]>([]);
  
  // Sort state
  type SortField = 'district' | 'fleet';
  type SortDirection = 'asc' | 'desc';
  const [sortField, setSortField] = useState<SortField>('fleet');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  
  // Modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState<FracJob | undefined>();
  
  // Tooltip state
  const [hoveredJob, setHoveredJob] = useState<FracJob | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  // Compute viewDays from start and end dates
  const viewDays = useMemo(() => {
    return Math.max(1, getDaysBetween(viewStartDate, viewEndDate));
  }, [viewStartDate, viewEndDate]);

  // Get unique fleets from jobs
  const allFleets = useMemo(() => {
    const fleets = new Set(jobs.map(j => j.fleet));
    return Array.from(fleets).sort((a, b) => {
      const [aNum] = a.split('|').map(Number);
      const [bNum] = b.split('|').map(Number);
      return aNum - bNum;
    });
  }, [jobs]);

  // Filtered data - now requires districts to be selected (empty = show nothing)
  const filteredJobs = useMemo(() => {
    return jobs.filter(job => {
      // If no districts selected, show nothing
      if (selectedDistricts.length === 0) {
        return false;
      }
      if (!selectedDistricts.includes(job.district)) {
        return false;
      }
      if (selectedFleets.length > 0 && !selectedFleets.includes(job.fleet)) {
        return false;
      }
      return true;
    });
  }, [jobs, selectedDistricts, selectedFleets]);

  // Helper to get primary district for a fleet
  const getFleetDistrict = (fleetId: string): District => {
    const fleetJobs = filteredJobs.filter(j => j.fleet === fleetId);
    return fleetJobs[0]?.district || 'Northeast';
  };

  const visibleFleets = useMemo(() => {
    const fleets = new Set(filteredJobs.map(j => j.fleet));
    const fleetsArray = Array.from(fleets);
    
    // Sort based on sortField and sortDirection
    return fleetsArray.sort((a, b) => {
      let comparison = 0;
      
      if (sortField === 'fleet') {
        // Sort by fleet number
        const [aNum] = a.split('|').map(Number);
        const [bNum] = b.split('|').map(Number);
        comparison = aNum - bNum;
      } else if (sortField === 'district') {
        // Sort by district name
        const aDistrict = getFleetDistrict(a);
        const bDistrict = getFleetDistrict(b);
        comparison = aDistrict.localeCompare(bDistrict);
      }
      
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [filteredJobs, sortField, sortDirection]);

  // Handle sort toggle
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Toggle direction
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      // Change field, reset to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Generate date headers
  const dateHeaders = useMemo(() => {
    const headers: { date: Date; label: string; isWeekStart: boolean; isMonthStart: boolean }[] = [];
    for (let i = 0; i < viewDays; i++) {
      const date = addDays(viewStartDate, i);
      headers.push({
        date,
        label: formatDate(date),
        isWeekStart: date.getDay() === 0,
        isMonthStart: date.getDate() === 1,
      });
    }
    return headers;
  }, [viewStartDate, viewDays]);

  // CRUD operations
  const handleAddJob = (jobData: Omit<FracJob, 'id'>) => {
    const newJob: FracJob = {
      ...jobData,
      id: `job-${Date.now()}`,
    };
    setJobs(prev => [...prev, newJob]);
    setShowAddModal(false);
    toast.success('Frac job added successfully');
  };

  const handleEditJob = (jobData: Omit<FracJob, 'id'> | FracJob) => {
    if ('id' in jobData) {
      setJobs(prev => prev.map(j => j.id === jobData.id ? jobData : j));
      setShowEditModal(false);
      setSelectedJob(undefined);
      toast.success('Frac job updated successfully');
    }
  };

  const handleDeleteJob = () => {
    if (selectedJob) {
      setJobs(prev => prev.filter(j => j.id !== selectedJob.id));
      setShowDeleteModal(false);
      setSelectedJob(undefined);
      toast.success('Frac job deleted successfully');
    }
  };

  // Calculate job position on timeline
  const getJobStyle = (job: FracJob): React.CSSProperties => {
    const startOffset = getDaysBetween(viewStartDate, job.startDate);
    const duration = getDaysBetween(job.startDate, job.endDate);
    const dayWidth = 100 / viewDays;
    
    // Clamp values to visible range
    const left = Math.max(0, startOffset * dayWidth);
    const width = Math.min((viewDays - startOffset) * dayWidth, duration * dayWidth);
    
    if (startOffset + duration < 0 || startOffset > viewDays) {
      return { display: 'none' };
    }
    
    const colors = DISTRICT_COLORS[job.district];
    
    return {
      left: `${left}%`,
      width: `${Math.max(width, 1)}%`,
      backgroundColor: colors.bg,
      borderColor: colors.border,
      color: colors.text,
    };
  };

  const handleJobHover = (job: FracJob | null, event?: React.MouseEvent) => {
    setHoveredJob(job);
    if (event && job) {
      const rect = (event.target as HTMLElement).getBoundingClientRect();
      setTooltipPosition({
        x: rect.left + rect.width / 2,
        y: rect.top - 10,
      });
    }
  };

  const handleDistrictFilter = (district: District) => {
    setSelectedDistricts(prev => 
      prev.includes(district) 
        ? prev.filter(d => d !== district)
        : [...prev, district]
    );
  };

  return (
    <section className={`${styles.section} ${isDark ? 'dark-theme' : 'light-theme'}`}>
      <ToastContainer position="top-right" autoClose={3000} theme={theme} />
      
      <div className={styles.profile}>
        <Header 
          title="Frac Calendar" 
        />

        <div className={styles.container}>
          <div className={styles.mainContent}>
            {/* Condensed Header - Centered */}
            <div className={styles.calendarHeader}>
              <div className={styles.headerCenter}>
                <div className={styles.datePickerWrapper}>
                  <label style={{ color: 'var(--color-text)', fontWeight: 500 }}>From:</label>
                  <DatePicker
                    selected={viewStartDate}
                    onChange={(date: Date | null) => date && setViewStartDate(date)}
                    showTimeSelect
                    dateFormat="MMM d, yyyy h:mm aa"
                    className="form-control"
                  />
                </div>
                
                <div className={styles.datePickerWrapper}>
                  <label style={{ color: 'var(--color-text)', fontWeight: 500 }}>To:</label>
                  <DatePicker
                    selected={viewEndDate}
                    onChange={(date: Date | null) => date && setViewEndDate(date)}
                    showTimeSelect
                    dateFormat="MMM d, yyyy h:mm aa"
                    className="form-control"
                    minDate={addDays(viewStartDate, 1)}
                  />
                </div>
                
                <button className={styles.btnPrimary} onClick={() => setShowAddModal(true)}>
                  <span>Add Job</span>
                </button>
              </div>
            </div>

            <div className={styles.calendarContainer}>
              {/* Main Calendar Grid */}
              <div className={styles.calendarGrid}>
                {/* Timeline Header */}
                <div className={styles.timelineHeader}>
                  <div className={styles.fleetColumn}>
                    <button 
                      className={styles.sortButton}
                      onClick={() => handleSort('district')}
                      title="Sort by District"
                    >
                      District
                      {sortField === 'district' && (
                        sortDirection === 'asc' ? <FiChevronUp size={12} /> : <FiChevronDown size={12} />
                      )}
                    </button>
                    <span className={styles.sortSeparator}>|</span>
                    <button 
                      className={styles.sortButton}
                      onClick={() => handleSort('fleet')}
                      title="Sort by Fleet"
                    >
                      Fleet
                      {sortField === 'fleet' && (
                        sortDirection === 'asc' ? <FiChevronUp size={12} /> : <FiChevronDown size={12} />
                      )}
                    </button>
                  </div>
                  <div className={styles.timelineColumns}>
                    {dateHeaders.map((header, idx) => (
                      <div 
                        key={idx}
                        className={`${styles.dateColumn} ${header.isMonthStart ? styles.monthStart : ''} ${header.isWeekStart ? styles.weekStart : ''}`}
                      >
                        {header.isMonthStart && (
                          <span className={styles.monthLabel}>
                            {header.date.toLocaleDateString('en-US', { month: 'short' })}
                          </span>
                        )}
                        {idx % 7 === 0 && (
                          <span className={styles.dateLabel}>{header.label}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Fleet Rows */}
                <div className={styles.fleetRows}>
                  {visibleFleets.map(fleet => {
                    const fleetJobs = filteredJobs.filter(j => j.fleet === fleet);
                    const primaryDistrict = fleetJobs[0]?.district || 'Northeast';
                    
                    return (
                      <div key={fleet} className={styles.fleetRow}>
                        <div 
                          className={styles.fleetLabel}
                          style={{ 
                            borderLeftColor: DISTRICT_COLORS[primaryDistrict].bg,
                          }}
                        >
                          <span className={styles.fleetName}>{fleet}</span>
                          <span 
                            className={styles.districtBadge}
                            style={{ 
                              backgroundColor: DISTRICT_COLORS[primaryDistrict].bg,
                              color: DISTRICT_COLORS[primaryDistrict].text,
                            }}
                          >
                            {primaryDistrict}
                          </span>
                        </div>
                        <div className={styles.timelineRow}>
                          {/* Grid lines */}
                          <div className={styles.gridLines}>
                            {dateHeaders.map((header, idx) => (
                              <div 
                                key={idx}
                                className={`${styles.gridLine} ${header.isWeekStart ? styles.weekLine : ''}`}
                              />
                            ))}
                          </div>
                          
                          {/* Jobs */}
                          {fleetJobs.map(job => (
                            <div
                              key={job.id}
                              className={styles.jobBar}
                              style={getJobStyle(job)}
                              onMouseEnter={(e) => handleJobHover(job, e)}
                              onMouseLeave={() => handleJobHover(null)}
                              onClick={() => {
                                setSelectedJob(job);
                                setShowEditModal(true);
                              }}
                            >
                              <span className={styles.jobLabel}>{job.operator}</span>
                            </div>
                          ))}
                        </div>
                        <div className={styles.rowActions}>
                          <button
                            className={styles.rowActionBtn}
                            onClick={() => {
                              const lastJob = fleetJobs[fleetJobs.length - 1];
                              setSelectedJob({
                                ...lastJob,
                                id: '',
                                fleet: fleet,
                                startDate: lastJob ? addDays(lastJob.endDate, 1) : new Date(),
                                endDate: lastJob ? addDays(lastJob.endDate, 15) : addDays(new Date(), 14),
                              } as FracJob);
                              setShowAddModal(true);
                            }}
                            title="Add job to this fleet"
                          >
                            <FiPlus size={14} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* District Legend - Bottom */}
              <div className={styles.districtLegend}>
                <span className={styles.districtLegendLabel}>DISTRICTS:</span>
                <div className={styles.districtChips}>
                  {districts.map(district => (
                    <button
                      key={district}
                      className={`${styles.districtChip} ${selectedDistricts.includes(district) ? styles.active : styles.inactive}`}
                      onClick={() => handleDistrictFilter(district)}
                      style={{
                        backgroundColor: selectedDistricts.includes(district) ? DISTRICT_COLORS[district].bg : undefined,
                        color: selectedDistricts.includes(district) ? DISTRICT_COLORS[district].text : undefined,
                        borderColor: DISTRICT_COLORS[district].bg,
                      }}
                    >
                      <span 
                        className={styles.districtColorDot}
                        style={{ backgroundColor: DISTRICT_COLORS[district].bg }}
                      />
                      {district}
                    </button>
                  ))}
                </div>
                <div className={styles.districtActions}>
                  <button 
                    className={styles.districtActionBtn}
                    onClick={() => setSelectedDistricts([...districts])}
                    title="Select All Districts"
                  >
                    Select All
                  </button>
                  <button 
                    className={styles.districtActionBtn}
                    onClick={() => setSelectedDistricts([])}
                    title="Clear All Districts (Hide All)"
                  >
                    Clear All
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tooltip */}
      {hoveredJob && (
        <div 
          className={styles.tooltip}
          style={{
            left: tooltipPosition.x,
            top: tooltipPosition.y,
            transform: 'translate(-50%, -100%)',
          }}
        >
          <div className={styles.tooltipHeader}>
            <span 
              className={styles.tooltipDistrict}
              style={{ 
                backgroundColor: DISTRICT_COLORS[hoveredJob.district].bg,
                color: DISTRICT_COLORS[hoveredJob.district].text,
              }}
            >
              {hoveredJob.district}
            </span>
            <span className={styles.tooltipFleet}>{hoveredJob.fleet}</span>
          </div>
          <div className={styles.tooltipContent}>
            <div className={styles.tooltipRow}>
              <span className={styles.tooltipLabel}>Operator</span>
              <span className={styles.tooltipValue}>{hoveredJob.operator}</span>
            </div>
            <div className={styles.tooltipRow}>
              <span className={styles.tooltipLabel}>Resource</span>
              <span className={styles.tooltipValue}>{hoveredJob.resource}</span>
            </div>
            <div className={styles.tooltipRow}>
              <span className={styles.tooltipLabel}>Start Date</span>
              <span className={styles.tooltipValue}>{formatFullDate(hoveredJob.startDate)}</span>
            </div>
            <div className={styles.tooltipRow}>
              <span className={styles.tooltipLabel}>End Date</span>
              <span className={styles.tooltipValue}>{formatFullDate(hoveredJob.endDate)}</span>
            </div>
            <div className={styles.tooltipRow}>
              <span className={styles.tooltipLabel}>Expected Stages/Day</span>
              <span className={styles.tooltipValue}>{hoveredJob.expectedStagesPerDay.toFixed(2)}</span>
            </div>
            <div className={styles.tooltipRow}>
              <span className={styles.tooltipLabel}>Revenue/Stage</span>
              <span className={styles.tooltipValue}>${hoveredJob.revenuePerStage.toLocaleString()}</span>
            </div>
            <div className={styles.tooltipRow}>
              <span className={styles.tooltipLabel}>Status</span>
              <span className={`${styles.tooltipValue} ${styles[`status_${hoveredJob.status}`]}`}>
                {hoveredJob.status.replace('-', ' ')}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      <FracJobModal
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setSelectedJob(undefined);
        }}
        onSave={handleAddJob}
        job={selectedJob}
        fleets={allFleets}
        mode="add"
      />
      
      <FracJobModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedJob(undefined);
        }}
        onSave={handleEditJob}
        onDelete={() => {
          setShowEditModal(false);
          setShowDeleteModal(true);
        }}
        job={selectedJob}
        fleets={allFleets}
        mode="edit"
      />
      
      <ConfirmDeleteModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedJob(undefined);
        }}
        onConfirm={handleDeleteJob}
        job={selectedJob}
      />
    </section>
  );
}

export default FracCalendar;
